import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/use-auth';
import { useCoinBalance } from '@/context/use-coin-balance';
import { FloatingNav } from '@/components/FloatingNav';
import { HeroCover } from '@/components/reader/HeroCover';
import { TOCDrawer } from '@/components/reader/TOCDrawer';
import { ReadingSettingsDrawer } from '@/components/reader/ReadingSettingsDrawer';
import { ChapterDivider } from '@/components/ChapterDivider';
import { Header } from '@/components/Header';
import { ChapterList } from '@/components/reader/ChapterList';
import { LockedChapterSection } from '@/components/reader/LockedChapterSection';
import { useChapterUnlock } from '@/hooks/use-chapter-unlock';
import { useChapterVisibility } from '@/hooks/use-chapter-visibility';
import { useReaderBook } from '@/hooks/use-reader-book';
import { useReadingAnalytics } from '@/hooks/use-reading-analytics';
import { usePullToUnlock } from '@/hooks/use-pull-to-unlock';

import { Chapter } from '@/types';

export function Reader() {
  const { bookId, chapterSeq } = useParams<{ bookId: string; chapterSeq: string }>();
  const navigate = useNavigate();
  const { user, signIn } = useAuth();
  const { refreshBalance } = useCoinBalance();

  const [showTOC, setShowTOC] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [readingSettings, setReadingSettings] = useState({
    fontSize: 18,
    lineHeight: 1.85,
    width: 640,
  });

  useEffect(() => {
    const stored = localStorage.getItem('readerSettings');
    if (!stored) return;
    try {
      const parsed = JSON.parse(stored) as Partial<typeof readingSettings>;
      setReadingSettings((prev) => ({
        fontSize: typeof parsed.fontSize === 'number' ? parsed.fontSize : prev.fontSize,
        lineHeight: typeof parsed.lineHeight === 'number' ? parsed.lineHeight : prev.lineHeight,
        width: typeof parsed.width === 'number' ? parsed.width : prev.width,
      }));
    } catch {
      // Ignore invalid localStorage payloads
    }
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--reading-font-size', `${readingSettings.fontSize}px`);
    root.style.setProperty('--reading-line-height', `${readingSettings.lineHeight}`);
    root.style.setProperty('--reading-width', `${readingSettings.width}px`);
    localStorage.setItem('readerSettings', JSON.stringify(readingSettings));
  }, [readingSettings]);
  const { bookData, book, sortedChapters } = useReaderBook({
    bookIdParam: bookId,
    clerkId: user?.id,
  });

  const {
    errorMessage,
    setErrorMessage,
    optimisticallyUnlockedChapters,
    handleUnlockChapter,
  } = useChapterUnlock({
    user,
    signIn,
    bookId,
    book,
    refreshBalance,
  });

  const { visibleChapter, setChapterRef } = useChapterVisibility({
    bookId,
    chapters: sortedChapters,
    initialChapterSeq: parseInt(chapterSeq || '1'),
  });

  const isChapterUnlocked = (chapter: Chapter) => {
    if (chapter.tokenCost === 0) return true;
    // Check optimistic unlocks first for instant UI updates
    if (optimisticallyUnlockedChapters.has(chapter._id)) return true;
    // Use server-side unlock status (secure)
    return chapter.isUnlocked === true;
  };

  const { hasStartedReading } = useReadingAnalytics({
    bookId,
    book,
    visibleChapter,
    chapters: sortedChapters,
    isChapterUnlocked,
  });

  const { pullProgress, resetPullProgress } = usePullToUnlock({
    chapters: sortedChapters,
    isChapterUnlocked,
    user,
    signIn,
    onUnlock: handleUnlockChapter,
  });

  if (!bookData) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)]">
        <div className="h-screen flex items-center justify-center">
          <div className="animate-pulse space-y-4 text-center">
            <div className="w-32 h-48 bg-[var(--bg-tertiary)] rounded-lg mx-auto" />
            <div className="h-8 bg-[var(--bg-tertiary)] rounded w-64 mx-auto" />
            <div className="h-4 bg-[var(--bg-tertiary)] rounded w-48 mx-auto" />
          </div>
        </div>
      </div>
    );
  }

  // Get visible chapters - only include unlocked chapters
  const visibleChapters: Chapter[] = [];
  let lastVisibleChapterIndex = -1;
  for (let i = 0; i < sortedChapters.length; i++) {
    const chapter = sortedChapters[i];
    // Stop at the first locked chapter (don't include it)
    if (!isChapterUnlocked(chapter) && chapter.tokenCost > 0) {
      break;
    }
    visibleChapters.push(chapter);
    lastVisibleChapterIndex = i;
  }

  // Only show the immediate next locked chapter after the last visible one
  const nextLockedChapter = lastVisibleChapterIndex >= 0 && lastVisibleChapterIndex + 1 < sortedChapters.length
    ? sortedChapters[lastVisibleChapterIndex + 1]
    : undefined;

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      
      {/* Error Message Toast */}
      {errorMessage && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[60] max-w-md w-full mx-4">
          <div className="bg-red-500/90 backdrop-blur-sm text-white px-6 py-4 rounded-xl shadow-2xl flex items-start gap-3 animate-in slide-in-from-top-2">
            <svg className="w-5 h-5 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              {errorMessage.startsWith('insufficient_tokens:') ? (
                <>
                  <p className="font-medium mb-2">Not enough coins</p>
                  <p className="text-sm text-white/80 mb-3">
                    You need more coins to unlock this chapter.
                  </p>
                  <button
                    onClick={() => navigate('/billing', { state: { returnUrl: `/book/${bookId}/${visibleChapter}` } })}
                    className="px-4 py-2 bg-[var(--accent)] text-[var(--bg-primary)] font-ui text-sm font-medium rounded-lg hover:bg-[var(--accent)]/90 transition-colors"
                  >
                    Get Coins
                  </button>
                </>
              ) : (
                <p className="font-medium">{errorMessage}</p>
              )}
            </div>
            <button 
              onClick={() => setErrorMessage(null)}
              className="text-white/80 hover:text-white shrink-0"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
      
      <HeroCover book={book} />
      
      <Header 
        showTOC={hasStartedReading}
        onOpenTOC={() => setShowTOC(true)}
      />

      <article className="transition-opacity duration-500">
        <div className="h-14" />
        
        <div className="reader-container py-8 pb-32">
          <ChapterList 
            chapters={visibleChapters}
            setChapterRef={setChapterRef}
          />

          {nextLockedChapter && (
            <LockedChapterSection
              chapter={nextLockedChapter}
              bookId={bookId}
              bookTitle={book?.title}
              pullProgress={pullProgress}
              onUnlock={() => {
                void handleUnlockChapter(nextLockedChapter).then((success) => {
                  if (success) {
                    resetPullProgress();
                  }
                });
              }}
            />
          )}

          {!nextLockedChapter && sortedChapters.length > 0 && (
            <div className="mt-24 text-center">
              <ChapterDivider isLast />
            </div>
          )}
        </div>
      </article>

      {hasStartedReading && (
        <FloatingNav
          onOpenTOC={() => setShowTOC(true)}
          onOpenSettings={() => setShowSettings(true)}
          currentChapter={visibleChapter}
          bookId={bookId}
        />
      )}

      <TOCDrawer
        isOpen={showTOC}
        onClose={() => setShowTOC(false)}
        bookId={bookId!}
        book={book}
        chapters={sortedChapters}
        currentChapterSeq={visibleChapter}
      />

      <ReadingSettingsDrawer
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        settings={readingSettings}
        onChange={setReadingSettings}
      />

    </div>
  );
}
